import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageSquare, Users, User, Hash } from 'lucide-react';
import { useAuth, API, getAuthHeaders } from '@/App';

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await fetch(`${API}/communities`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setGroups(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    } finally {
      setLoading(false);
    }
  };

  const userName = user?.name?.split(' ')[0] || 'Student';

  return (
    <div className="space-y-6 md:space-y-8 pb-20 md:pb-0" data-testid="home-page">
      {/* Welcome Banner - Purple Gradient */}
      <div className="relative bg-gradient-to-r from-purple-600 via-purple-500 to-violet-500 dark:from-purple-600 dark:via-purple-500 dark:to-violet-500 rounded-2xl p-6 md:p-10 overflow-hidden shadow-md">
        <div className="relative text-center text-white">
          <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-3" style={{ fontFamily: 'DM Sans' }}>
            Hey, {userName} 👋
          </h1>
          <p className="text-sm md:text-base text-white/95 mb-4 md:mb-6 max-w-xl mx-auto">
            Jump into your communities, ask questions, or chat with classmates
          </p>
          <Button 
            data-testid="explore-communities-btn"
            onClick={() => navigate('/communities')}
            className="bg-[#1a1a1a] text-white hover:bg-[#252525] dark:bg-[#1a1a1a] dark:hover:bg-[#252525] rounded-xl px-6 md:px-8 h-10 md:h-11 text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            Explore Communities
          </Button>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        <div 
          data-testid="feature-card-communities"
          onClick={() => navigate('/communities')}
          className="bg-gray-50 dark:bg-[#141414] rounded-xl p-4 md:p-6 border border-gray-200/50 dark:border-[#2a2a2a] hover:border-indigo-200 dark:hover:border-purple-500/50 hover:bg-indigo-50/30 dark:hover:bg-purple-500/10 transition-all duration-200 cursor-pointer group"
        >
          <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-100 dark:bg-purple-500/20 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:bg-indigo-200 dark:group-hover:bg-purple-500/30 transition-colors">
            <Users className="w-5 h-5 md:w-6 md:h-6 text-indigo-600 dark:text-purple-400" strokeWidth={2.5} />
          </div>
          <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white mb-1 md:mb-2" style={{ fontFamily: 'DM Sans' }}>
            Communities
          </h3>
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            Join class discussions and collaborate with peers
          </p>
        </div>

        <div 
          data-testid="feature-card-messages"
          onClick={() => navigate('/messages')}
          className="bg-gray-50 dark:bg-[#141414] rounded-xl p-4 md:p-6 border border-gray-200/50 dark:border-[#2a2a2a] hover:border-indigo-200 dark:hover:border-purple-500/50 hover:bg-indigo-50/30 dark:hover:bg-purple-500/10 transition-all duration-200 cursor-pointer group"
        >
          <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-100 dark:bg-purple-500/20 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:bg-indigo-200 dark:group-hover:bg-purple-500/30 transition-colors">
            <MessageSquare className="w-5 h-5 md:w-6 md:h-6 text-indigo-600 dark:text-purple-400" strokeWidth={2.5} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'DM Sans' }}>
            Messages
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            Check your direct messages and replies
          </p>
        </div>

        <div 
          data-testid="feature-card-profile"
          onClick={() => navigate('/profile')}
          className="bg-gray-50 dark:bg-[#141414] rounded-xl p-6 border border-gray-200/50 dark:border-[#2a2a2a] hover:border-indigo-200 dark:hover:border-purple-500/50 hover:bg-indigo-50/30 dark:hover:bg-purple-500/10 transition-all duration-200 cursor-pointer group"
        >
          <div className="w-12 h-12 bg-indigo-100 dark:bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-indigo-200 dark:group-hover:bg-purple-500/30 transition-colors">
            <User className="w-6 h-6 text-indigo-600 dark:text-purple-400" strokeWidth={2.5} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'DM Sans' }}>
            Profile
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            View your profile and update settings
          </p>
        </div>
      </div>

      {/* Quick Access Communities - Shows recent/top communities */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'DM Sans' }}>
            Your Communities
          </h2>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/communities')}
            className="text-indigo-600 dark:text-purple-400 hover:text-indigo-700 dark:hover:text-purple-300 hover:bg-indigo-50 dark:hover:bg-purple-500/10 text-sm font-semibold"
          >
            View All
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 dark:border-purple-500"></div>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-[#141414] rounded-xl border border-gray-200/50 dark:border-[#2a2a2a]">
            <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No communities yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Communities will appear here when you join them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groups.slice(0, 4).map((group) => (
              <div
                key={group.community_id}
                data-testid={`home-community-${group.community_id}`}
                onClick={() => navigate(`/community/${group.community_id}`)}
                className="bg-gray-50 dark:bg-[#141414] rounded-xl p-4 border border-gray-200/50 dark:border-[#2a2a2a] hover:border-indigo-200 dark:hover:border-purple-500/50 hover:bg-indigo-50/30 dark:hover:bg-purple-500/10 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 bg-indigo-100/50 dark:bg-purple-500/20"
                  >
                    <Hash className="w-6 h-6 text-indigo-600 dark:text-purple-400" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate" style={{ fontFamily: 'DM Sans' }}>
                      {group.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{group.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="mt-16 pt-8 border-t border-gray-200 dark:border-[#2a2a2a]">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/privacy')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-purple-400 transition-colors">
              Privacy
            </button>
            <button onClick={() => navigate('/terms')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-purple-400 transition-colors">
              Terms
            </button>
            <button onClick={() => navigate('/suggestions')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-purple-400 transition-colors">
              Suggestions
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center md:text-right max-w-lg">
            UniConnect is a student-built platform and is not affiliated with or endorsed by Toronto Metropolitan University.
          </p>
        </div>
      </footer>
    </div>
  );
}